import { Activiteit, GekoppeldeInzet, Station, stationConflict } from '@/components/detailPanel/types/types';
import { isZelfdeDienstregelpunt } from '@/utils/dienstregelpunt';

// Returns the departure uitvoeringsTijd if present, otherwise arrival uitvoeringsTijd (full ISO datetime strings)
export function getStationTime(station: Station): string | undefined {
  return station?.vertrekUitvoeringsTijd ?? station?.aankomstUitvoeringsTijd;
}

// Parses a time string (HH:mm) to minutes since midnight
export function parseTimeToMinutes(timeStr?: string) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

// Creates a Date object with the given time string (HH:mm) on today
export function createDateFromTimeStr(timeStr?: string): Date | null {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const now = new Date();
  now.setHours(hours, minutes, 0, 0);
  return now;
}

// Helper to format a date string to 'HH:mm'
function formatTime(dateStr?: string): string {
  return dateStr ? new Date(dateStr).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';
}

// Helper to calculate delay/early status
function getDelayStatus(gepland?: string, uitvoering?: string) {
  const geplandDate = gepland ? new Date(gepland) : undefined;
  const uitvoeringDate = uitvoering ? new Date(uitvoering) : undefined;
  const diffMinutes =
    geplandDate && uitvoeringDate ? Math.abs((uitvoeringDate.getTime() - geplandDate.getTime()) / 60000) : 0;
  return {
    // should be delayed/early if the execution time is after the planned time and the difference is more than 1 minute
    delayed: geplandDate && uitvoeringDate ? uitvoeringDate > geplandDate && diffMinutes >= 1 : false,
    early: geplandDate && uitvoeringDate ? uitvoeringDate < geplandDate && diffMinutes >= 1 : false,
  };
}

// Zoekt alle conflicten voor hetzelfde dienstregelpunt, met tijdsmatching
// wanneer het traject meerdere stops heeft bij hetzelfde dienstregelpunt.
function findMatchingConflicts(
  conflicten: stationConflict[],
  activiteit: Activiteit,
  allActiviteiten: Activiteit[],
): stationConflict[] {
  if (conflicten.length === 0) return [];

  const zelfdeDienstregelpunt = conflicten.filter((conflict) =>
    isZelfdeDienstregelpunt(conflict.dienstregelpunt, activiteit.dienstregelpunt),
  );

  if (zelfdeDienstregelpunt.length === 0) return [];

  // Controleer of dit dienstregelpunt meerdere keren voorkomt in het traject
  const activiteitenZelfdeDrp = allActiviteiten.filter((a) =>
    isZelfdeDienstregelpunt(a.dienstregelpunt, activiteit.dienstregelpunt),
  );

  // Enkele stationsgroep: geef alle conflicten bij dit dienstregelpunt terug
  if (activiteitenZelfdeDrp.length <= 2) return zelfdeDienstregelpunt;

  // Meerdere stationsgroepen bij hetzelfde dienstregelpunt: neem alleen conflicten mee
  // waarvan deze activiteit de dichtstbijzijnde is (op basis van tijd)
  const referentietijd = new Date(activiteit.actueelGeplandeTijd).getTime();

  return zelfdeDienstregelpunt.filter((conflict) => {
    const conflictTijd = new Date(conflict.tijd).getTime();
    const myDiff = Math.abs(conflictTijd - referentietijd);

    return !activiteitenZelfdeDrp.some((other) => {
      if (other === activiteit) return false;
      const otherDiff = Math.abs(conflictTijd - new Date(other.actueelGeplandeTijd).getTime());
      return otherDiff < myDiff;
    });
  });
}

// Helper: Extract spoor info
function extractSpoorInfo(item: any) {
  const geplandSpoorObj = item?.oorspronkelijkeSporen?.[0];
  const actueelSpoorObj = item?.actueleSporen?.[0];
  return {
    geplandSpoor: geplandSpoorObj
      ? `${geplandSpoorObj.dienstregelpuntspoornaam ?? ''}${geplandSpoorObj.laatstBezetSpoor ? `-${geplandSpoorObj.laatstBezetSpoor}` : ''}`
      : '',
    actueelSpoor: actueelSpoorObj
      ? `${actueelSpoorObj.dienstregelpuntspoornaam ?? ''}${actueelSpoorObj.laatstBezetSpoor ? `-${actueelSpoorObj.laatstBezetSpoor}` : ''}`
      : '',
  };
}

// Helper: Build base station object
function buildBaseStation(item: any) {
  return {
    name: item.dienstregelpunt,
    prognose: item.prognose,
    materieelKnoop: item.isKnoop ?? undefined,
  };
}

// Helper: Create station for AANKOMST
function createArrivalStation(
  item: any,
  delayed: boolean,
  early: boolean,
  spoor: any,
  conflicts: stationConflict[],
  isKnoopStationWithKnoopStopBefore: boolean,
) {
  return {
    ...buildBaseStation(item),
    arrival: formatTime(item.uitvoeringsTijd),
    arrivalDelayed: delayed,
    arrivalIsEarly: early,
    aankomstOorspronkelijkGeplandeTijd: item.oorspronkelijkGeplandeTijd,
    aankomstActueelGeplandeTijd: item.actueelGeplandeTijd,
    aankomstUitvoeringsTijd: item.uitvoeringsTijd,
    geplandSpoor: spoor.geplandSpoor,
    actueelSpoor: spoor.actueelSpoor,
    conflicts,
    isKnoopStationWithKnoopStopBefore,
  };
}

// Helper: Create station for VERTREK
function createDepartureStation(
  item: any,
  delayed: boolean,
  early: boolean,
  spoor: any,
  conflicts: stationConflict[],
  isKnoopStationWithKnoopStopBefore: boolean,
) {
  return {
    ...buildBaseStation(item),
    arrival: '',
    departure: formatTime(item.uitvoeringsTijd),
    departureDelayed: delayed,
    departureIsEarly: early,
    vertrekOorspronkelijkGeplandeTijd: item.oorspronkelijkGeplandeTijd,
    vertrekActueelGeplandeTijd: item.actueelGeplandeTijd,
    vertrekUitvoeringsTijd: item.uitvoeringsTijd,
    geplandSpoor: spoor.geplandSpoor,
    actueelSpoor: spoor.actueelSpoor,
    conflicts,
    isKnoopStationWithKnoopStopBefore,
  };
}

// Helper: Create station for KORTE_STOP
function createShortStopStation(
  item: any,
  delayed: boolean,
  early: boolean,
  spoor: any,
  conflicts: stationConflict[],
  isKnoopStationWithKnoopStopBefore: boolean,
) {
  return {
    ...buildBaseStation(item),
    arrival: formatTime(item.uitvoeringsTijd),
    departure: formatTime(item.uitvoeringsTijd),
    arrivalDelayed: delayed,
    arrivalIsEarly: early,
    departureDelayed: delayed,
    departureIsEarly: early,
    aankomstOorspronkelijkGeplandeTijd: item.oorspronkelijkGeplandeTijd,
    aankomstActueelGeplandeTijd: item.actueelGeplandeTijd,
    aankomstUitvoeringsTijd: item.uitvoeringsTijd,
    vertrekOorspronkelijkGeplandeTijd: item.oorspronkelijkGeplandeTijd,
    vertrekActueelGeplandeTijd: item.actueelGeplandeTijd,
    vertrekUitvoeringsTijd: item.uitvoeringsTijd,
    geplandSpoor: spoor.geplandSpoor,
    actueelSpoor: spoor.actueelSpoor,
    conflicts,
    isKnoopStationWithKnoopStopBefore,
  };
}

// Helper: Add prognose fields to materieelKnoop stations
export function addPrognoseFields(stations: Station[]) {
  let i = 0;
  while (i < stations.length) {
    const station = stations[i];
    if (station.materieelKnoop) {
      const prognoses: number[] = [];
      let j = i;
      if (typeof station.prognose === 'number') {
        prognoses.push(station.prognose);
      }
      while (j + 1 < stations.length && stations[j + 1].materieelKnoop === false) {
        j++;
        const nextPrognose = stations[j].prognose;
        if (typeof nextPrognose === 'number') {
          prognoses.push(nextPrognose);
        }
      }
      if (prognoses.length > 0) {
        station.lowestPrognose = Math.min(...prognoses);
        station.highestPrognose = Math.max(...prognoses);
      }
      i = j + 1;
    } else {
      i++;
    }
  }
}

// Main function: Transforms an array of detailInzet route activiteiten into Station[]
export function transformStations(
  data?:
    | {
        activiteitId: string;
        soort: string;
        dienstregelpunt: string;
        oorspronkelijkGeplandeTijd: string;
        actueelGeplandeTijd: string;
        uitvoeringsTijd: string;
        afrijrichting: string;
        volgnummer: number;
        prognose: number;
        isKnoop: boolean;
        oorspronkelijkeSporen?: { dienstregelpuntspoornaam?: string; laatstBezetSpoor?: string }[];
        actueleSporen?: { dienstregelpuntspoornaam?: string; laatstBezetSpoor?: string }[];
        materieelKnoop?: boolean;
      }[]
    | null,
  conflicten?: stationConflict[],
): Station[] {
  if (!data?.length) return [];

  const stations: Station[] = [];
  let pendingArrival: Station | null = null;

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const { delayed, early } = getDelayStatus(item.oorspronkelijkGeplandeTijd, item.uitvoeringsTijd);
    const spoor = extractSpoorInfo(item);
    const conflicts = findMatchingConflicts(conflicten ?? [], item, data);
    const isKnoopStationWithKnoopStopBefore = stations.length > 0 ? !!stations.at(-1)?.materieelKnoop : false;

    if (item.soort === 'AANKOMST') {
      pendingArrival = createArrivalStation(item, delayed, early, spoor, conflicts, isKnoopStationWithKnoopStopBefore);
      stations.push(pendingArrival);
    } else if (item.soort === 'VERTREK') {
      if (pendingArrival) {
        // Samenvoegen van aankomst- en vertrekconflicten, ontdubbeld op type+dienstregelpunt+tijd
        const mergedConflicts = [...(pendingArrival.conflicts ?? [])];
        for (const c of conflicts) {
          if (
            !mergedConflicts.some(
              (m) =>
                m.type === c.type && isZelfdeDienstregelpunt(m.dienstregelpunt, c.dienstregelpunt) && m.tijd === c.tijd,
            )
          ) {
            mergedConflicts.push(c);
          }
        }
        Object.assign(pendingArrival, {
          departure: formatTime(item.uitvoeringsTijd),
          departureDelayed: delayed,
          departureIsEarly: early,
          vertrekOorspronkelijkGeplandeTijd: item.oorspronkelijkGeplandeTijd,
          vertrekActueelGeplandeTijd: item.actueelGeplandeTijd,
          vertrekUitvoeringsTijd: item.uitvoeringsTijd,
          prognose: item.prognose,
          geplandSpoor: spoor.geplandSpoor,
          actueelSpoor: spoor.actueelSpoor,
          conflicts: mergedConflicts,
          isKnoopStationWithShortStopBefore:
            pendingArrival.isKnoopStationWithKnoopStopBefore ?? isKnoopStationWithKnoopStopBefore,
        });
        pendingArrival = null;
      } else {
        stations.push(
          createDepartureStation(item, delayed, early, spoor, conflicts, isKnoopStationWithKnoopStopBefore),
        );
      }
    } else if (item.soort === 'KORTE_STOP' || (item.soort === 'DOORKOMST' && (i === 0 || i === data.length - 1))) {
      stations.push(createShortStopStation(item, delayed, early, spoor, conflicts, isKnoopStationWithKnoopStopBefore));
      pendingArrival = null;
    }
  }

  addPrognoseFields(stations);

  return stations;
}

// Finds the index of the first station with a datetime after the referenceDate
export function findFirstFutureStationIndex(
  stations: Station[],
  referenceDate: Date | null,
  getStationTimeFn: (station: Station) => string | undefined,
): number {
  return stations.findIndex((station) => {
    if (!referenceDate) return false;
    const timeStr = getStationTimeFn(station);
    if (!timeStr) return false;
    return new Date(timeStr) > referenceDate;
  });
}

export function getSetHeightTimeline(pastLength: number, futureLength: number) {
  return {
    applySetHeightToPast: pastLength > 0 && futureLength === 0,
    applySetHeightToFuture: (futureLength > 0 && pastLength > 0) || (futureLength > 0 && pastLength === 0),
  };
}

export function mapInzetPositieToShortString(positie: number): string {
  switch (positie) {
    case 0:
      return '-V';
    case 1:
      return '-M';
    case 100:
      return '-A';
    default:
      return '';
  }
}
export function sortGekoppeldeInzetten(a: GekoppeldeInzet, b: GekoppeldeInzet): number {
  const getPriority = (pos: string | number | undefined) => {
    const n = Number(pos);
    if (n === 0) return 0;
    if (n === 1) return 1;
    if (n === 100) return 99;
    return 2;
  };
  const prioA = getPriority(a.inzetPositie);
  const prioB = getPriority(b.inzetPositie);
  if (prioA !== prioB) return prioA - prioB;
  if (prioA === 2) {
    const numA = a.inzetPositie ? Number(a.inzetPositie) : Infinity;
    const numB = b.inzetPositie ? Number(b.inzetPositie) : Infinity;
    return numA - numB;
  }
  return 0;
}

export function getTimelineClassName(
  showAllStops: boolean,
  baseClass: string,
  isKnoopStationWithKnoopStopBefore?: boolean,
) {
  return showAllStops || isKnoopStationWithKnoopStopBefore ? baseClass : `${baseClass} show-only-knoop`;
}
